import { useCallback, useEffect, useMemo, useState } from 'react'
import Dialog from 'core/components/dialogs/Dialog'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import Button from 'core/components/buttons/Button'
import cn from 'utils/cn'
import { isSalesDemoEnv } from 'app/settings/defaultSetting'
import Tabs, { TabItem } from 'core/components/tabs/Tabs'
import FeatureFlagTab from 'app/feature-flag/FeatureFlagsTab'
import ProdAccountsTab from 'app/feature-flag/ProdAccountsTab'
import SalesTab from 'app/feature-flag/SalesTab'
import { isItDemoAccount } from 'app/feature-flag/utils'

type TabsType = 'ff' | 'accounts' | 'sales'

/**
 * @description: A widget to control feature flags in the app. Enabled by secret hot-key.
 */
const FeatureFlagController = () => {
  const [tab, setTab] = useState<TabsType>('ff')
  const [show, setShow] = useState(false)
  const toggleShow = useCallback(() => setShow(!show), [show])

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'm') && (e.metaKey || e.ctrlKey)) {
        toggleShow()
      }
    }
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  }, [toggleShow])

  const { data: user } = useUserProfile()

  const isLocalhost = window.location.hostname.includes('localhost')
  const isEverlanceAccount = /@everlance.com$|@test.com$/.test(
    user?.email ?? ''
  )
  const isStage = import.meta.env.VITE_APP_API_BASE_URL.includes('stage')
  const isMac = /mac/i.test(navigator.userAgent)
  const shouldRender = isLocalhost
    ? true
    : user?.email && (isEverlanceAccount || isStage)
  const isSalesRep =
    isSalesDemoEnv && user?.email && isItDemoAccount(user.email)

  const tabItems: TabItem[] = useMemo(() => {
    const items: TabItem[] = [
      {
        key: 'ff',
        label: 'Feature flags',
        content: <FeatureFlagTab />
      },
      {
        key: 'prod',
        label: 'Prod Accounts',
        content: <ProdAccountsTab />
      }
    ]
    if (isSalesRep) {
      items.push({
        key: 'sales',
        label: 'Sales',
        content: <SalesTab />
      })
    }
    return items
  }, [isSalesRep])

  useEffect(() => {
    if (isSalesRep) {
      setTab('sales')
    }
  }, [isSalesRep])

  if (!shouldRender) return null

  return (
    <>
      <Button
        className={cn(
          'fixed top-2 size-[34px] aspect-square p-0! flex items-center justify-center rounded-full! z-50',
          show ? 'right-[26.625rem] bg-white ' : 'right-2 bg-black/10 '
        )}
        icon={show ? 'close' : 'flag'}
        onPointerDown={() => setShow((s) => !s)}
      />
      <Dialog
        className="top-2 right-2"
        show={show}
        onClose={() => setShow(false)}
        hasBackdrop
      >
        <div className="relative card bg-white w-100 max-h-[calc(100svh-1rem)] overflow-auto">
          <div className="flex justify-between items-center mb-5">
            <span className="font-semibold text-lg">Config center </span>{' '}
            <span className="rounded-md text-xs ml-4 bg-dark-1 p-1 font-bold">
              {isMac ? '⌘' : 'CTRL'} + K/M
            </span>
          </div>

          <Tabs
            activeTab={tab}
            onChange={(_t) => {
              setTab(_t as TabsType)
            }}
            items={tabItems}
          />
        </div>
      </Dialog>
    </>
  )
}

export default FeatureFlagController
